import React, {
  PropsWithChildren,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import { AccessDenied, AccessDeniedReasonsEnum } from 'shared/errors/access-denied'
import { getContentType } from 'modules/course/course-step-router/hook'
import { useCourseQuiz } from 'modules/course/hooks/use-course-quiz'
import useStepRouter from 'modules/course/hooks/use-step-router'
import { CourseQuizId } from 'modules/course/types/course-quiz-interface'
import { isCourseQuizDataResult } from 'modules/course/utils/is-course-quiz-data-result'
import { CourseQuizStateContextInterface, CourseStatusEnum, QuizStateType } from './types'

const defaultState: CourseQuizStateContextInterface = {
  quizState: { status: CourseStatusEnum.in_progress },
  setQuizState: () => {},
}

export const CourseQuizStateContext =
  React.createContext<CourseQuizStateContextInterface>(defaultState)

interface CourseQuizStateContextProviderProps extends PropsWithChildren {
  quizId: CourseQuizId
}

export const CourseQuizStateContextProvider = ({
  children,
  quizId,
}: CourseQuizStateContextProviderProps): ReactNode => {
  const [quizState, setQuizState] = useState<QuizStateType | null>(null)
  const { step } = useStepRouter()

  const { route } = useRouter()
  const contentType = getContentType(route)

  const onProgressLocked = useCallback((error: AccessDenied) => {
    const data = error.response?.data
    const reason =
      typeof data === 'object' && data !== null && 'reason' in data
        ? (data as { reason?: string }).reason
        : undefined

    const isLocked = reason === AccessDeniedReasonsEnum.BlockedByProgress

    if (isLocked) {
      return
    }

    throw error
  }, [])

  const { courseQuizData, mutate } = useCourseQuiz({
    courseQuizId: quizId,
    shouldFetch: contentType === 'quiz',
    accessDeniedResponseHandler: onProgressLocked,
  })

  useEffect(() => {
    if (step?.id !== quizId) return

    if (step?.completed) {
      setQuizState({ status: CourseStatusEnum.results })
    } else {
      setQuizState({ status: CourseStatusEnum.in_progress })
    }
  }, [step?.id, step?.completed, quizId])

  useEffect(() => {
    if (
      (!quizState || quizState?.status === CourseStatusEnum.results) &&
      courseQuizData &&
      !isCourseQuizDataResult(courseQuizData)
    ) {
      mutate()
    }
  }, [quizState, courseQuizData, mutate])

  const contextValue = useMemo(
    () => ({
      quizState,
      setQuizState,
    }),
    [quizState, setQuizState],
  )

  return (
    <CourseQuizStateContext.Provider value={contextValue}>
      {children}
    </CourseQuizStateContext.Provider>
  )
}
